<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AppToast from './AppToast.vue'
import { useToasts } from './toast-store'
import { TOAST_POSITIONS, type ToastPosition } from './toast-types'

const props = withDefaults(
  defineProps<{
    /** Default corner for toasts that don't pin their own `position`. */
    position?: ToastPosition
    /** Keep the stack permanently expanded (otherwise it expands on hover/focus). */
    expand?: boolean
    visibleToasts?: number
    duration?: number
    closeButton?: boolean
  }>(),
  {
    position: 'bottom-center',
    expand: false,
    visibleToasts: 3,
    duration: 4000,
    closeButton: false,
  },
)

const { t } = useI18n()

let toasterMounted = false
const isActive = ref(false)
let isOwner = false

onMounted(() => {
  if (toasterMounted) {
    console.warn(
      '[AppToaster] Multiple <AppToaster> instances mounted — only the first renders. ' +
        'Mount exactly one at the app root.',
    )
    return
  }
  toasterMounted = true
  isOwner = true
  isActive.value = true
})

onBeforeUnmount(() => {
  if (isOwner) {
    toasterMounted = false
    isOwner = false
    isActive.value = false
  }
})

const toasts = useToasts()

const POSITION_META: Record<
  ToastPosition,
  { corner: string; yPosition: 'top' | 'bottom'; xPosition: 'left' | 'center' | 'right' }
> = {
  'top-left': { corner: 'top-12 left-12', yPosition: 'top', xPosition: 'left' },
  'top-center': {
    corner: 'top-12 left-1/2 -translate-x-1/2',
    yPosition: 'top',
    xPosition: 'center',
  },
  'top-right': { corner: 'top-12 right-12', yPosition: 'top', xPosition: 'right' },
  'bottom-left': { corner: 'bottom-12 left-12', yPosition: 'bottom', xPosition: 'left' },
  'bottom-center': {
    corner: 'bottom-12 left-1/2 -translate-x-1/2',
    yPosition: 'bottom',
    xPosition: 'center',
  },
  'bottom-right': { corner: 'bottom-12 right-12', yPosition: 'bottom', xPosition: 'right' },
}

const heights = reactive(new Map<number | string, number>())
function setHeight(id: number | string, height: number) {
  heights.set(id, height)
}

const hovered = reactive(Object.fromEntries(TOAST_POSITIONS.map((p) => [p, false]))) as Record<
  ToastPosition,
  boolean
>

watch(toasts, (list) => {
  const live = new Set(list.map((t) => t.id))
  for (const id of heights.keys()) {
    if (!live.has(id)) heights.delete(id)
  }
})

const groups = computed(() =>
  TOAST_POSITIONS.map((pos) => {
    const meta = POSITION_META[pos]
    const list = toasts.value.filter((t) => (t.position ?? props.position) === pos && !t.dismissed)
    const isExpanded = hovered[pos] || props.expand
    const total = list.length

    let ahead = 0
    const items = list.map((toast, index) => {
      const heightBefore = ahead
      ahead += heights.get(toast.id) ?? 0
      return { toast, index, heightBefore }
    })

    const firstHeight = list[0] ? (heights.get(list[0].id) ?? 0) : 0
    const heightCss =
      total === 0
        ? 'var(--spacing-0)'
        : isExpanded
          ? `calc(${ahead}px + var(--spacing-6) * ${total - 1})`
          : `${firstHeight}px`

    return { pos, meta, items, total, isExpanded, heightCss }
  }),
)
</script>

<template>
  <Teleport v-if="isActive" to="body">
    <TransitionGroup
      v-for="group in groups"
      :key="group.pos"
      tag="ol"
      enter-active-class="animate-toast-in"
      leave-active-class="animate-toast-out"
      :aria-label="t('primitives.toaster.notifications')"
      :class="[
        'fixed z-50 m-0 flex w-(--toast-width) max-w-[calc(100vw-var(--spacing-16))] list-none flex-col p-0',
        'transition-[height] duration-moderate ease-soft',
        group.meta.corner,
      ]"
      :style="{ height: group.heightCss }"
      @mouseenter="hovered[group.pos] = true"
      @mouseleave="hovered[group.pos] = false"
      @focusin="hovered[group.pos] = true"
      @focusout="hovered[group.pos] = false"
    >
      <AppToast
        v-for="item in group.items"
        :key="item.toast.id"
        :toast="item.toast"
        :index="item.index"
        :total="group.total"
        :expanded="group.isExpanded"
        :height-before="item.heightBefore"
        :y-position="group.meta.yPosition"
        :x-position="group.meta.xPosition"
        :visible-toasts="visibleToasts"
        :close-button="closeButton"
        :global-duration="duration"
        @height="setHeight"
      />
    </TransitionGroup>
  </Teleport>
</template>
